/**
 * The frame a capture is recorded in, and the codec it is recorded with.
 *
 * Always the best the screen gives: a screen is recorded at its own size, up
 * to 4K, and a camera alone at its own size up to 1080p. There is no quality
 * to choose at the recorder: this is the first generation, every edit and
 * every export is made from it, so it is given everything, and the choice of
 * a smaller size or a lighter file belongs to the export and the download,
 * where it costs nothing. The bitrate follows the pixel count, generously
 * (about 12 Mbps at 1080p, 40 at most): screen content is mostly still, so the
 * encoder uses little of it, but a scroll or a window being dragged gets what
 * it needs and text stays sharp. The server then keeps a copy at
 * near-original constant quality that is a fraction of the size. The sound is
 * Opus at 128 kbit/s, where it is clean.
 *
 * The codec is H.264 wherever the browser offers it (Chrome, Edge): most
 * machines encode it in hardware, and a hardware encoder never falls
 * behind, which is what keeps the cursor and the scrolling smooth in the
 * recording. VP9 in software does fall behind at 1080p on a laptop, and a
 * MediaRecorder that falls behind drops frames: the recording judders.
 * VP8 is the next choice — half the work of VP9 — and VP9 the last.
 */

export type CaptureMode = 'screen' | 'camera'

export interface CaptureProfile {
  width: number
  height: number
  frameRate: number
  videoBitsPerSecond: number
  audioBitsPerSecond: number
}

/** The largest a screen is recorded: 4K. A bigger one is scaled down to fit, keeping its shape. */
export const CAPTURE_MAX = { width: 3840, height: 2160 } as const
/** The largest a camera alone is recorded: 1080p. */
export const CAMERA_MAX = { width: 1920, height: 1080 } as const
/** The size assumed for a source that has not said how big it is. */
const UNKNOWN_SCREEN = { width: 1920, height: 1080 } as const
const UNKNOWN_CAMERA = { width: 1280, height: 720 } as const

/** Best first. */
export const CAPTURE_MIMES: readonly string[] = [
  'video/webm;codecs=h264,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm;codecs=vp9,opus',
  'video/webm',
  'video/mp4',
]

/** The MIME type the browser will record a capture with; null when it cannot record at all. */
export function pickCaptureMime(
  supported: (type: string) => boolean = (t) =>
    typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t),
): string | null {
  return CAPTURE_MIMES.find((c) => supported(c)) ?? null
}

const even = (n: number): number => Math.max(2, Math.round(n / 2) * 2)
const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v))

export function captureProfile(input: {
  mode: CaptureMode
  /** How big the source says it is (a screen's capture, or the camera's own picture). */
  sourceWidth?: number | null
  sourceHeight?: number | null
}): CaptureProfile {
  const camera = input.mode === 'camera'
  const max = camera ? CAMERA_MAX : CAPTURE_MAX
  const unknown = camera ? UNKNOWN_CAMERA : UNKNOWN_SCREEN
  const sw = input.sourceWidth && input.sourceWidth > 0 ? input.sourceWidth : unknown.width
  const sh = input.sourceHeight && input.sourceHeight > 0 ? input.sourceHeight : unknown.height
  // Its own size; only one bigger than the most is scaled down, keeping its shape.
  const scale = Math.min(1, max.width / sw, max.height / sh)
  const width = even(sw * scale)
  const height = even(sh * scale)
  const perPixel = camera ? 0.15 : 0.2
  const [low, high] = camera ? [4_000_000, 10_000_000] : [8_000_000, 40_000_000]
  return {
    width,
    height,
    frameRate: 30,
    videoBitsPerSecond: Math.round(clamp(width * height * 30 * perPixel, low, high)),
    audioBitsPerSecond: 128_000,
  }
}
